/* global GM_STATE */
import { useEffect, useRef, useState } from 'preact/hooks'
import { formatMoney } from 'libs/shopify'
import {
  STORAGE_1P_KEY,
  STORAGE_2P_KEY,
  formatPreorderDate,
  getMetafieldKey,
  getDispatchDuration,
  getStockStatus,
  parseStockJson,
  resolveActiveWarehouse
} from 'helpers/stock-status'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import Icon from 'snippets/icon/icon'

const STORAGE_POSTCODE_KEY = 'location_postcode'

// Maps an Australian postcode to its state abbreviation.
// Ported from the legacy product-shipping.js asset.
function getStateAu(pc) {
  const n = parseInt(pc, 10)
  const exceptions = {
    '0872': 'NT',
    2540: 'NSW',
    2611: 'ACT',
    2620: 'NSW',
    3500: 'VIC',
    3585: 'VIC',
    3586: 'VIC',
    3644: 'VIC',
    3707: 'VIC',
    2899: 'NSW',
    6798: 'WA',
    6799: 'WA',
    7151: 'TAS'
  }
  if (exceptions[String(pc)] !== undefined) return exceptions[String(pc)]
  const ranges = {
    NSW: [[1000, 2599], [2619, 2898], [2921, 2999]],
    ACT: [[200, 299], [2600, 2618], [2900, 2920]],
    VIC: [[3000, 3999], [8000, 8999]],
    QLD: [[4000, 4999], [9000, 9999]],
    SA: [[5000, 5999]],
    WA: [[6000, 6797], [6800, 6999]],
    TAS: [[7000, 7999]],
    NT: [[800, 999]]
  }
  for (const [state, pairs] of Object.entries(ranges)) {
    for (const [lo, hi] of pairs) {
      if (n >= lo && n <= hi) return state
    }
  }
  return ''
}

// Serialises a nested object to URLSearchParams entries, mirroring jQuery $.param.
function flattenParams(obj, prefix) {
  const entries = []
  for (const [key, value] of Object.entries(obj)) {
    const paramKey = prefix ? `${prefix}[${key}]` : key
    if (Array.isArray(value)) {
      value.forEach((item, i) => {
        const arrayKey = `${paramKey}[${i}]`
        if (item !== null && typeof item === 'object') {
          entries.push(...flattenParams(item, arrayKey))
        } else {
          entries.push([arrayKey, String(item ?? '')])
        }
      })
    } else if (value !== null && typeof value === 'object') {
      entries.push(...flattenParams(value, paramKey))
    } else {
      entries.push([paramKey, String(value ?? '')])
    }
  }
  return entries
}

function ProductShipping() {
  const t = window.translate.location
  const notifyMeWhenAvailable = GM_STATE.apps.backInStock.notifyMeWhenAvailable
  const klaviyoNotifyPopupId = GM_STATE.apps.backInStock.klaviyoNotifyPopupId
  const [warehouse1P, setWarehouse1P] = useState(() => localStorage.getItem(STORAGE_1P_KEY) || null)
  const [warehouse2P, setWarehouse2P] = useState(() => localStorage.getItem(STORAGE_2P_KEY) || null)
  const [currentVariant, setCurrentVariant] = useState(() => window.productState.initProduct.initVariant)
  const [postcode, setPostcode] = useState(() => localStorage.getItem(STORAGE_POSTCODE_KEY) || null)
  const [shippingRates, setShippingRates] = useState(null)
  const [isLoadingRates, setIsLoadingRates] = useState(false)

  useEffect(() => {
    const onPostcodeUpdated = (e) => {
      setWarehouse1P(e.detail.warehouse1P ?? null)
      setWarehouse2P(e.detail.warehouse2P ?? null)
      setPostcode(e.detail.postcode)
    }
    const onVariantUpdated = (variant) => setCurrentVariant(variant)

    window.addEventListener('postcode:updated', onPostcodeUpdated)
    globalEmitter.on(emitterProps.product.updateVariant, onVariantUpdated)

    return () => {
      window.removeEventListener('postcode:updated', onPostcodeUpdated)
      globalEmitter.off(emitterProps.product.updateVariant, onVariantUpdated)
    }
  }, [])

  useEffect(() => {
    if (!postcode || !currentVariant) {
      setShippingRates(null)
      return
    }

    const controller = new AbortController()

    const fetchRates = async () => {
      setIsLoadingRates(true)
      setShippingRates(null)

      const product = window.productState.initProduct
      const item = {
        product_id: String(currentVariant.product_id || product.id),
        variant_id: String(currentVariant.id),
        sku: currentVariant.sku || '',
        type: '',
        tag: Array.isArray(product.tags) ? product.tags.join(',') : (product.tags || ''),
        name: product.title || '',
        quantity: '1',
        grams: String(currentVariant.weight || 0),
        price: String(currentVariant.price || 0),
        vendor: product.vendor || ''
      }

      const rate = {
        destination: {
          postal_code: postcode,
          province: getStateAu(postcode),
          country: 'AU'
        },
        items: [item]
      }

      try {
        const params = new URLSearchParams(flattenParams({ rate, shop: Shopify.shop }))
        const res = await fetch('https://sbz.cirkleinc.com/front-widget', {
          method: 'POST',
          body: params,
          signal: controller.signal
        })
        const data = await res.json()
        if (!controller.signal.aborted) {
          setShippingRates(Array.isArray(data?.rates) ? data.rates : [])
        }
      } catch {
        if (!controller.signal.aborted) {
          setShippingRates([])
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoadingRates(false)
        }
      }
    }

    fetchRates()

    return () => controller.abort()
  }, [postcode, currentVariant])

  const lowStockThreshold = window.productState.lowStockThreshold
  const lowStockMessage = window.productState.lowStockMessage
  const lowStockMessageOverride = window.productState.lowStockMessageOverride

  const hasPostcodeInput = warehouse1P !== null || warehouse2P !== null
  const activeWarehouse = hasPostcodeInput
    ? resolveActiveWarehouse(warehouse1P, warehouse2P, currentVariant)
    : null

  const metafieldKey = activeWarehouse ? getMetafieldKey(activeWarehouse) : null
  const rawStockJson = metafieldKey ? currentVariant.metafields?.[metafieldKey] : null
  const stockData = rawStockJson ? parseStockJson(rawStockJson) : null

  const { isPreorder, isOutOfStock, isLowStock, isInStock } = getStockStatus(stockData, currentVariant, lowStockThreshold)

  // Bundle parents have no warehouse blob, so the flags above fall back to the parent's Shopify
  // availability and would contradict ProductATC, which owns bundle status (derived from the
  // children). Keep the postcode row and shipping rates here, drop the status rows.
  const isBundle = !!window.productState?.initProduct?.metafields?.bundle_child_skus
  const showStatusRows = !isBundle
  const showBackInStock = isOutOfStock && !isBundle

  const preorderDateLabel = formatPreorderDate(stockData)

  const shippingToMessage = postcode ? t.product.shipping_to.replace('{postcode}', postcode) : ''

  const buttonAriaLabel = postcode
    ? `${t.cta.delivering_to} ${postcode}`
    : t.cta.set_postcode

  const containerRef = useRef(null)

  // Sync the host element's accessible label whenever delivery state changes.
  useEffect(() => {
    if (!containerRef.current) return
    const host = containerRef.current.closest('product-shipping')
    if (host) host.setAttribute('aria-label', buttonAriaLabel)
  }, [buttonAriaLabel])

  return (
    <>
      <div
        ref={containerRef}
        className="flex items-start justify-between w-full gap-2 text-xs font-normal text-current lg:text-sm"
      >
        <span className="flex items-start gap-2">
          {activeWarehouse && postcode ? (
            <>
              {showStatusRows && isPreorder ? (
                <>
                  <span className="flex items-center justify-center flex-shrink-0 relative -top-[1px] lg:top-[1px]" aria-hidden="true">
                    <Icon name="icon-info" viewBox="0 0 20 20" className="w-4 h-4 lg:w-[20px] lg:h-[20px]" />
                  </span>
                  <span className="font-[550]">{t.product.on_preorder}</span>
                </>
              ) : showStatusRows && isOutOfStock ? (
                <>
                  <span className="flex items-center justify-center flex-shrink-0 relative -top-[1px] lg:top-[1px]" aria-hidden="true">
                    <Icon name="icon-info" viewBox="0 0 20 20" className="w-4 h-4 lg:w-[20px] lg:h-[20px]" />
                  </span>
                  <span className="font-[550]">{t.product.out_of_stock}</span>
                </>
              ) : (
                <>
                  <span className="flex items-center justify-center flex-shrink-0 relative -top-[1px] lg:top-[1px]" aria-hidden="true">
                    <Icon name="icon-truck-thin" viewBox="0 0 20 20" className="w-4 h-4 lg:w-[20px] lg:h-[20px]" />
                  </span>
                  <span className="font-[550]">{shippingToMessage}</span>
                </>
              )}
            </>
          ) : (
            <>
              <span className="flex items-center justify-center flex-shrink-0 relative -top-[1px] lg:top-[1px]" aria-hidden="true">
                <Icon name="icon-truck-thin" viewBox="0 0 20 20" className="w-4 h-4 lg:w-[20px] lg:h-[20px]" />
              </span>
              <span className="flex flex-col items-start text-left">
                <span className="font-[550]">{t.callout.choose_title}</span>
                {!warehouse1P && !warehouse2P && (
                  <span className="text-grey-400">
                    {t.callout.choose_subtext}
                  </span>
                )}
              </span>
            </>
          )}
        </span>
        <span className="flex-shrink-0 relative top-[2px]" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 16" className="icon w-4 h-4 lg:w-[20px] lg:h-[20px]">
            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.4" d="m5.75 3.5 4.5 4.5-4.5 4.5" />
          </svg>
        </span>
      </div>

      <div role="status" aria-live="polite" aria-atomic="false">
        {activeWarehouse && (
          <div className="mt-3">
            <div className="text-sm pl-7">
              {/* Priority: Preorder → Low Stock → In Stock → Out of Stock */}
              {!showStatusRows ? null : isPreorder ? (
                <div data-product-status className="relative">
                  <span className="absolute flex-shrink-0 inline-block w-[10px] h-[10px] rounded-full -left-[22px] top-[7px] bg-attention-bg" aria-hidden="true"></span>
                  {preorderDateLabel && (
                    <p className="text-grey-400">{t.callout.preorder} <span className="font-[550]">{preorderDateLabel}</span></p>
                  )}
                </div>
              ) : isLowStock ? (
                <div data-product-status>
                  <span className="flex relative items-center gap-1.5">
                    <span className="absolute flex-shrink-0 inline-block w-[10px] h-[10px] rounded-full -left-[22px] top-[7px] bg-warning-content" aria-hidden="true"></span>
                    <span className="font-[550]">Only {currentVariant.inventory_quantity} left</span>
                  </span>
                  <p className="text-grey-400">{lowStockMessageOverride !== null ? lowStockMessageOverride : lowStockMessage}</p>
                </div>
              ) : isInStock ? (
                <div data-product-status>
                  <span className="flex relative items-center gap-1.5">
                    <span className="absolute flex-shrink-0 inline-block w-[10px] h-[10px] rounded-full -left-[22px] top-[7px] bg-success-dark" aria-hidden="true"></span>
                    <span className="font-[550]">{t.product.in_stock}</span>
                  </span>
                  <p className="text-grey-400">
                    <>{t.callout.dispatch_message} <span className="font-[550]">{getDispatchDuration(activeWarehouse, t.callout)}</span></>
                  </p>
                </div>
              ) : isOutOfStock ? (
                <div data-product-status>
                  <span className="relative text-grey-400">
                    <span className="absolute flex-shrink-0 inline-block w-[10px] h-[10px] rounded-full -left-[22px] top-1 bg-error-content" aria-hidden="true"></span>
                    {t.product.out_of_stock_message}
                  </span>
                </div>
              ) : null
              }
            </div>
            {!showBackInStock ? (
              <div className="pt-4 mt-4 border-t border-light">
                {isLoadingRates ? (
                  <div className="flex flex-col gap-2 animate-pulse">
                    <div className="w-full h-4 rounded bg-grey-200"></div>
                    <div className="w-3/4 h-4 rounded bg-grey-200"></div>
                  </div>
                ) : shippingRates && shippingRates.length > 0 ? (
                  <dl className="flex flex-col gap-1 text-sm">
                    {shippingRates.map((rate) => (
                      <div key={rate.service_name} className="flex justify-between">
                        <dt>{rate.service_name}</dt>
                        {!rate.description?.includes('DO NOT PROCEED') && (
                          <dd className="font-[550]">{formatMoney(rate.total_price)}</dd>
                        )}
                      </div>
                    ))}
                  </dl>
                ) : null}
              </div>
            ) : (
              <button
                type="button"
                className="w-full mt-4 button-primary klaviyo-bis-trigger"
                title={notifyMeWhenAvailable}
                onClick={e => e.stopPropagation()}
                klaviyo-bis-trigger
              >
                {notifyMeWhenAvailable}
              </button>
            )}
          </div>
        )}
      </div>
    </>
  )
}

export default ProductShipping
